/**
 * Today-so-far live strip. Sits above the Overview KPI grid and polls
 * every 30 seconds for a near-real-time snapshot of activity, errors,
 * and support tickets.
 */
import React from 'react';
import {
  Activity,
  AlertTriangle,
  LifeBuoy,
  MessageSquare,
  Radio,
  UserPlus,
} from 'lucide-react';
import { useAnalyticsQuery } from '../hooks/useAnalyticsQuery';
import { cn } from '../../../../lib/cn';
import { formatInt } from '../format';

interface LiveStripData {
  as_of: string;
  signups_today: number;
  active_now: number;
  sessions_today: number;
  workouts_today: number;
  chat_messages_today: number;
  errors_today: number;
  tickets_today: number;
}

interface TileDef {
  label: string;
  value: number;
  icon: React.ElementType;
  severity?: 'ok' | 'warn' | 'bad';
}

function severity(label: 'errors_today' | 'tickets_today', value: number): 'ok' | 'warn' | 'bad' {
  if (label === 'errors_today') {
    if (value >= 25) return 'bad';
    if (value >= 5) return 'warn';
    return 'ok';
  }
  if (value >= 10) return 'warn';
  return 'ok';
}

export const LiveStrip: React.FC = () => {
  const { data, isLoading, error } = useAnalyticsQuery<LiveStripData>(
    ['live-strip'],
    '/api/admin/analytics/live-strip',
    { refetchInterval: 30_000, staleTime: 0, refetchOnWindowFocus: true },
  );

  const d = data?.data;

  const tiles: TileDef[] = [
    // Backend window widened to 60 min (active_window_minutes) — the 1-minute
    // count was near-always 0–2 at current scale and carried no signal.
    { label: 'Active (last hour)', value: d?.active_now ?? 0, icon: Radio },
    { label: 'Signups today', value: d?.signups_today ?? 0, icon: UserPlus },
    // 3-layer (workouts + mobility + daily routines) — matches the "Sessions
    // completed" KPI definition everywhere else (audit F2).
    { label: 'Sessions today', value: d?.sessions_today ?? 0, icon: Activity },
    { label: 'Chat messages today', value: d?.chat_messages_today ?? 0, icon: MessageSquare },
    {
      label: 'Errors today',
      value: d?.errors_today ?? 0,
      icon: AlertTriangle,
      severity: severity('errors_today', d?.errors_today ?? 0),
    },
    {
      label: 'Support tickets',
      value: d?.tickets_today ?? 0,
      icon: LifeBuoy,
      severity: severity('tickets_today', d?.tickets_today ?? 0),
    },
  ];

  return (
    <div className="rounded-lg border border-gray-200 bg-gradient-to-br from-coach-cedar-subtle/50 to-white p-3 shadow-sm">
      <div className="mb-2 flex items-center justify-between px-1">
        <div className="flex items-center gap-2">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
          </span>
          <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-600">
            Today so far
          </span>
          {data?.data?.as_of && (
            <span className="text-[11px] text-gray-400">· {data.data.as_of}</span>
          )}
        </div>
        {error && (
          <span className="text-[11px] text-rose-500">Live feed unavailable</span>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
        {tiles.map(({ label, value, icon: Icon, severity: sev }) => {
          const sevClasses =
            sev === 'bad'
              ? 'text-rose-700'
              : sev === 'warn'
              ? 'text-amber-700'
              : 'text-gray-800';
          return (
            <div
              key={label}
              className="flex items-center gap-2.5 rounded-md bg-white px-3 py-2 shadow-sm ring-1 ring-gray-100"
            >
              <Icon className="h-4 w-4 text-gray-400" aria-hidden />
              <div className="min-w-0 flex-1">
                <div className="truncate text-[10.5px] font-semibold uppercase tracking-wide text-gray-500">
                  {label}
                </div>
                <div
                  className={cn(
                    'text-base font-bold tabular-nums',
                    sevClasses,
                    isLoading && 'animate-pulse text-gray-300',
                  )}
                >
                  {isLoading ? '—' : formatInt(value)}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
